import React, { useCallback } from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, SxProps, Theme } from '@mui/material';

import { HangerList, LumberList, OrganizationMaterialSet, PlateList } from '@paragon/contract-library';
import { assertExhaustiveSwitch, humanCase } from '@paragon/utilities';

import { loadCurrentOrganizationMaterialSet } from '../actions/materialListActions';
import { sortMaterialLists } from '../sortMaterialLists';
import { useMaterialListsDispatch, useMaterialListsSelector } from './hooks';

type MaterialListType = 'lumber' | 'plate' | 'hanger';

function getListsOfType(set: OrganizationMaterialSet, type: MaterialListType) {
  switch (type) {
    case 'lumber':
      return Object.values(set.lumberLists);

    case 'plate':
      return Object.values(set.plateLists);

    case 'hanger':
      return Object.values(set.hangerLists);

    default:
      assertExhaustiveSwitch(type);
  }
}

interface Props {
  readonly type: MaterialListType;
  readonly selectedGuid: string;

  readonly sx?: SxProps<Theme>;
  readonly size?: 'small' | 'medium' | undefined;
  readonly fullWidth?: boolean;

  readonly disabled?: boolean;
  readonly required?: boolean;

  readonly onEdit: (newSelectedGuid: string) => void;
}

export const MaterialListSelect: React.FunctionComponent<Props> = ({
  type,
  selectedGuid,
  sx,
  size,
  fullWidth,
  disabled,
  required,
  onEdit,
}) => {
  const designInventory = useMaterialListsSelector((state) => state.materialLists);

  const dispatch = useMaterialListsDispatch();
  const retryDownload = useCallback(() => dispatch(loadCurrentOrganizationMaterialSet()), [dispatch]);

  const fieldName = `${type}-list`;
  const lists = (designInventory.state === 'loaded' ? getListsOfType(designInventory.item, type) : []) as (
    LumberList | PlateList | HangerList
  )[];

  const listsToShow = lists.filter((list) => !list.isArchived);

  if (!listsToShow.some((list) => list.guid === selectedGuid)) {
    const archivedList = lists.find((list) => list.guid === selectedGuid);
    if (archivedList) listsToShow.push(archivedList);
  }

  return (
    <>
      <FormControl
        size={size ?? 'small'}
        variant="outlined"
        fullWidth={fullWidth ?? true}
        required={required}
        error={required && selectedGuid === ''}
        disabled={disabled || designInventory.state !== 'loaded'}
        sx={sx}
      >
        <InputLabel htmlFor={`${fieldName}-input`}>{humanCase(fieldName)}</InputLabel>
        <Select
          id={`${type}-list-input`}
          value={selectedGuid}
          onChange={(event) => onEdit(event.target.value as string)}
          label={humanCase(fieldName)}
        >
          {sortMaterialLists(listsToShow).map((list) => (
            <MenuItem key={list.guid} value={list.guid} disabled={list.isArchived}>
              {list.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {designInventory.state === 'error' ? (
        <Button variant="outlined" onClick={retryDownload}>
          Retry
        </Button>
      ) : null}
    </>
  );
};
